import { useState, useEffect } from 'react';

import { apiService } from '../services/apiServices';
import { useUserRole } from './useUserRole';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PermissionEntry {
  key: string;
  name: string;
  allowed: boolean;
}

interface PrivilegeEntry {
  key: string;
  name: string;
  permissions: PermissionEntry[];
}

interface UserPermissionsData {
  userId: string;
  privileges: PrivilegeEntry[];
}

export interface UserPrivileges {
  canListFacilities: boolean;
  canCreateFacility: boolean;
  canEditFacility: boolean;
  canListFacilityUser: boolean;
  canCreateFacilityUser: boolean;
  canEditFacilityUser: boolean;
  canCreateEquipment: boolean;
  canEditEquipment: boolean;
  canListEquipment: boolean;
  canToggleEmployeeStatus: boolean;
  canCreateContactRequest: boolean;
  canCreateServiceRequest: boolean;
  canCancelServiceRequest: boolean;
  /** TECHNICIAN only — whether the technician can accept or reject an assigned task. */
  canAcceptRejectTask: boolean;
  /** TECHNICIAN-only — gates the "Update Task Status" action (RR-624). */
  canUpdateTaskStatus: boolean;
  /** TECHNICIAN only — whether the technician can view assigned task (SR) details. */
  canViewTask: boolean;
}

export interface UseUserPrivilegesResult {
  privileges: UserPrivileges;
  isLoading: boolean;
  error: string | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PERMISSIONS_ENDPOINT = 'policy-management/permissions';

const DEFAULT_PRIVILEGES: UserPrivileges = {
  canListFacilities: false,
  canCreateFacility: false,
  canEditFacility: false,
  canListFacilityUser: false,
  canCreateFacilityUser: false,
  canEditFacilityUser: false,
  canCreateEquipment: false,
  canEditEquipment: false,
  canListEquipment: false,
  canToggleEmployeeStatus: false,
  canCreateContactRequest: false,
  canCreateServiceRequest: false,
  canCancelServiceRequest: false,
  canAcceptRejectTask: false,
  canUpdateTaskStatus: false,
  canViewTask: false,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parsePrivileges = (privileges: PrivilegeEntry[]): UserPrivileges => {
  const facilityPrivilege = privileges.find((p) => p.key === 'manage_facility');
  const facilityUserPrivilege = privileges.find(
    (p) => p.key === 'manage_facility_user'
  );
  const equipmentPrivilege = privileges.find(
    (p) => p.key === 'manage_equipment'
  );
  const facilityListPermission = facilityPrivilege?.permissions.find(
    (p) => p.key === 'facilityList'
  );
  const facilityCreatePermission = facilityPrivilege?.permissions.find(
    (p) => p.key === 'facilityCreate'
  );
  const facilityEditPermission = facilityPrivilege?.permissions.find(
    (p) => p.key === 'facilityEdit'
  );
  const facilityUserListPermission = facilityUserPrivilege?.permissions.find(
    (p) => p.key === 'facilityUserList'
  );
  const facilityUserCreatePermission = facilityUserPrivilege?.permissions.find(
    (p) => p.key === 'facilityUserCreate'
  );
  const facilityUserEditPermission = facilityUserPrivilege?.permissions.find(
    (p) => p.key === 'facilityUserEdit'
  );
  const equipmentCreatePermission = equipmentPrivilege?.permissions.find(
    (p) => p.key === 'equipmentCreate'
  );
  const equipmentEditPermission = equipmentPrivilege?.permissions.find(
    (p) => p.key === 'equipmentEdit'
  );
  const equipmentListPermission = equipmentPrivilege?.permissions.find(
    (p) => p.key === 'equipmentList'
  );
  const facilityUserActivateDeactivatePermission =
    facilityUserPrivilege?.permissions.find(
      (p) => p.key === 'facilityUserActivateDeactivate'
    );
  const allPermissions = privileges.flatMap((p) => p.permissions);
  const contactRequestCreatePermission = allPermissions.find(
    (p) => p.key === 'contactRequestCreate'
  );
  const serviceRequestPrivilege = privileges.find(
    (p) => p.key === 'manage_service_request'
  );
  const serviceRequestCreatePermission =
    serviceRequestPrivilege?.permissions.find(
      (p) => p.key === 'serviceRequestCreate'
    );
  const serviceRequestCancelPermission =
    serviceRequestPrivilege?.permissions.find(
      (p) => p.key === 'serviceRequestCancel'
    );
  // TECHNICIAN: manage_assigned_tasks → taskAcceptReject / taskUpdateStatus / taskView
  const taskPrivilege = privileges.find(
    (p) => p.key === 'manage_assigned_tasks'
  );
  const taskAcceptRejectPermission = taskPrivilege?.permissions.find(
    (p) => p.key === 'taskAcceptReject'
  );
  const taskUpdateStatusPermission = taskPrivilege?.permissions.find(
    (p) => p.key === 'taskUpdateStatus'
  );
  const taskViewPermission = taskPrivilege?.permissions.find(
    (p) => p.key === 'taskView'
  );
  return {
    canListFacilities: facilityListPermission?.allowed ?? false,
    canCreateFacility: facilityCreatePermission?.allowed ?? false,
    canEditFacility: facilityEditPermission?.allowed ?? false,
    canListFacilityUser: facilityUserListPermission?.allowed ?? false,
    canCreateFacilityUser: facilityUserCreatePermission?.allowed ?? false,
    canEditFacilityUser: facilityUserEditPermission?.allowed ?? false,
    canCreateEquipment: equipmentCreatePermission?.allowed ?? false,
    canEditEquipment: equipmentEditPermission?.allowed ?? false,
    canListEquipment: equipmentListPermission?.allowed ?? false,
    canToggleEmployeeStatus:
      facilityUserActivateDeactivatePermission?.allowed ?? false,
    canCreateContactRequest: contactRequestCreatePermission?.allowed ?? false,
    canCreateServiceRequest: serviceRequestCreatePermission?.allowed ?? false,
    canCancelServiceRequest: serviceRequestCancelPermission?.allowed ?? false,
    canAcceptRejectTask: taskAcceptRejectPermission?.allowed ?? false,
    canUpdateTaskStatus: taskUpdateStatusPermission?.allowed ?? false,
    canViewTask: taskViewPermission?.allowed ?? false,
  };
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Fetches the authenticated user's permissions from the backend
 * (`policy-management/permissions`) and exposes parsed boolean privilege flags.
 *
 * - **CLIENT** — returns CLIENT AVP template-linked permissions.
 * - **TECHNICIAN** — returns technician_policies DB row parsed into privilege flags.
 *   Key flags: `canAcceptRejectTask` (accept/reject button) and
 *   `canUpdateTaskStatus` (update-status button) — RR-620 / RR-624.
 * - Other roles — resolves immediately with all-false defaults (no API call).
 *
 * Extend `UserPrivileges` and `parsePrivileges()` as new permission checks are needed.
 */
export const useUserPrivileges = (): UseUserPrivilegesResult => {
  const { isClient, isTechnician, isLoading: roleLoading } = useUserRole();
  const [privileges, setPrivileges] =
    useState<UserPrivileges>(DEFAULT_PRIVILEGES);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Wait for role to resolve before deciding whether to fetch
    if (roleLoading) {
      return;
    }

    // CLIENT and TECHNICIAN both have AVP-gated actions in the UI (client:
    // create/cancel SR; technician: accept/reject + update task status).
    // Other roles resolve immediately with default (all-false) privileges.
    if (!isClient && !isTechnician) {
      setIsLoading(false);
      return;
    }

    let mounted = true;

    const fetchPrivileges = async (): Promise<void> => {
      try {
        const result = await apiService.getAPI(PERMISSIONS_ENDPOINT);
        if (!mounted) {
          return;
        }

        if (result.error) {
          setError('Nie udało się pobrać uprawnień.');
          setIsLoading(false);
          return;
        }

        const data = result.data as { data: UserPermissionsData } | undefined;
        if (data?.data?.privileges) {
          setPrivileges(parsePrivileges(data.data.privileges));
        }
        setIsLoading(false);
      } catch {
        if (mounted) {
          setError('Nie udało się pobrać uprawnień.');
          setIsLoading(false);
        }
      }
    };

    void fetchPrivileges();

    return () => {
      mounted = false;
    };
  }, [isClient, isTechnician, roleLoading]);

  return { privileges, isLoading, error };
};
